import { useApi } from '@backstage/core-plugin-api';
import { sev2ApiRef } from '@internal/backstage-plugin-sev2/src/api';
import { Task } from '@internal/backstage-plugin-sev2/src/types';
import { useEffect, useState } from 'react';
import { debounce } from '@mui/material/utils';
import { TaskSearchParams } from '@internal/backstage-plugin-sev2';

const useSev2TaskCombobox: (projectPHID: string | undefined) => {
  loading: boolean;
  open: boolean;
  options: Task[];
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOptions: React.Dispatch<React.SetStateAction<Task[]>>;
  searchTask: (search: string) => Promise<void>;
} = (projectPHID: string | undefined) => {
  const api = useApi(sev2ApiRef);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Task[]>([]);
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      const searchPayload: TaskSearchParams = {
        projects: [projectPHID ?? ''],
      };
      const response = await api.searchTasks({ searchParams: searchPayload });

      if (active) {
        setOptions(response.result.data);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, loading, projectPHID]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const searchTask = debounce(async (search: string) => {
    const normalizedSearch = search.trim();
    const isIdSearch =
      /^\d+$/.test(normalizedSearch) || /^T\d+$/i.test(normalizedSearch);
    const numericSearch = normalizedSearch.replace(/^T/i, '');

    const searchPayload: TaskSearchParams = {
      projects: [projectPHID ?? ''],
      ...(isIdSearch
        ? { idLike: numericSearch }
        : { titleLike: normalizedSearch }),
    };
    const response = await api.searchTasks({ searchParams: searchPayload });

    setOptions(response.result.data);
  }, 500);

  return {
    loading,
    open,
    options,
    setOpen,
    setOptions,
    searchTask,
  };
};

export default useSev2TaskCombobox;
